/**
 * ColumnSchemaTable Component
 *
 * Displays table column schema with watermark candidate highlighting
 * Allows selection of watermark column via radio buttons
 */

import { TableColumn } from '../../lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Star, Check } from 'lucide-react';

interface ColumnSchemaTableProps {
  columns: TableColumn[];
  selectedColumn: string | null;
  onSelectColumn: (columnName: string) => void;
  recommendedColumn: string | null;
  allPotentialColumns: string | null;
}

export function ColumnSchemaTable({
  columns,
  selectedColumn,
  onSelectColumn,
  recommendedColumn,
  allPotentialColumns
}: ColumnSchemaTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">Select</TableHead>
            <TableHead>Column Name</TableHead>
            <TableHead>Data Type</TableHead>
            <TableHead className="text-center">Nullable</TableHead>
            <TableHead className="text-center">Watermark Candidate</TableHead>
            <TableHead className="text-center">Position</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {columns.map((column) => {
            const isRecommended = column.columnName === recommendedColumn;
            const isSelected = column.columnName === selectedColumn;

            return (
              <TableRow
                key={column.columnName}
                className={`cursor-pointer hover:bg-muted/50 ${
                  isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                } ${
                  isRecommended ? 'bg-green-50/50 dark:bg-green-900/10' : ''
                }`}
                onClick={() => onSelectColumn(column.columnName)}
              >
                <TableCell>
                  <RadioGroup
                    value={selectedColumn || ''}
                    onValueChange={onSelectColumn}
                  >
                    <div className="flex items-center">
                      <RadioGroupItem
                        value={column.columnName}
                        id={`radio-${column.columnName}`}
                      />
                    </div>
                  </RadioGroup>
                </TableCell>

                <TableCell>
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-mono">{column.columnName}</code>
                    {isRecommended && (
                      <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                        <Star className="h-3 w-3 mr-1 fill-green-600" />
                        Recommended
                      </Badge>
                    )}
                    {isSelected && !isRecommended && (
                      <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                        <Check className="h-3 w-3 mr-1" />
                        Selected
                      </Badge>
                    )}
                  </div>
                </TableCell>

                <TableCell>
                  <code className="text-xs bg-muted px-2 py-0.5 rounded">
                    {column.dataType}
                  </code>
                </TableCell>

                <TableCell className="text-center">
                  {column.nullable ? (
                    <Badge variant="outline">Yes</Badge>
                  ) : (
                    <Badge variant="secondary">No</Badge>
                  )}
                </TableCell>

                <TableCell className="text-center">
                  {column.isWatermarkCandidate ? (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      Yes
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">
                      No
                    </Badge>
                  )}
                </TableCell>

                <TableCell className="text-center text-muted-foreground">
                  {column.columnId}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Analysis Section */}
      <div className="border-t mt-4">
        {recommendedColumn && (
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border-b">
            <div className="flex items-start gap-2">
              <Star className="h-5 w-5 text-green-600 fill-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-green-900 dark:text-green-100 text-sm mb-2">
                  Recommended Watermark Column
                </p>
                <code className="text-base font-mono bg-green-100 dark:bg-green-900/40 px-2 py-1 rounded text-green-900 dark:text-green-100">
                  {recommendedColumn}
                </code>
              </div>
            </div>
          </div>
        )}

        {allPotentialColumns && (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-b">
            <p className="font-semibold text-blue-900 dark:text-blue-100 text-sm mb-2">
              All Potential Watermark Columns
            </p>
            <div className="flex flex-wrap gap-2">
              {allPotentialColumns.split(',').map((col, idx) => {
                const columnName = col.trim();
                const isRecommended = columnName === recommendedColumn;
                return (
                  <Badge
                    key={idx}
                    variant="outline"
                    className={
                      isRecommended
                        ? 'bg-green-100 text-green-800 border-green-300'
                        : 'bg-blue-100 text-blue-800 border-blue-300'
                    }
                  >
                    <code className="font-mono text-xs">{columnName}</code>
                    {isRecommended && <Star className="h-3 w-3 ml-1 fill-green-600" />}
                  </Badge>
                );
              })}
            </div>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
              These are all date/timestamp columns found in this table. Check if any have unusual names that might be suitable for watermarking.
            </p>
          </div>
        )}

        <div className="p-4 bg-muted/30 text-sm text-muted-foreground">
          <p className="font-medium mb-2">Watermark Candidate Criteria:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Column data type is DATE, TIMESTAMP, or DATETIME</li>
            <li>Column name contains: MODIFIED, UPDATED, LAST_MODIFIED, CHANGE_DATE, or CREATED</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
